
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQuery } from '@tanstack/react-query';

export type DocumentSubmission = {
  id: string;
  title: string;
  description: string | null;
  document_type: string;
  state: string | null;
  regulatory_authority: string | null;
  submission_period: string | null;
  submission_year: number | null;
  submission_quarter: number | null;
  status: string;
  file_path: string;
  file_name: string;
  file_size: number | null;
  file_type: string | null;
  metadata: Record<string, any> | null;
  submitted_by: string;
  submitted_at: string;
  updated_at: string;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
};

type SubmitDocumentParams = {
  title: string;
  description?: string;
  document_type: string;
  state?: string;
  regulatory_authority?: string;
  submission_period?: string;
  submission_year?: number;
  submission_quarter?: number;
  file: File;
  metadata?: Record<string, any>;
};

export function useDocumentSubmissions() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchDocuments = async () => {
    const { data, error } = await supabase
      .from('document_submissions')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) throw error;
    return data as DocumentSubmission[];
  };

  const submitDocument = async (params: SubmitDocumentParams) => {
    try {
      setIsLoading(true);

      // 1. Upload the file to storage
      const fileExt = params.file.name.split('.').pop();
      const filePath = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, params.file);

      if (uploadError) throw uploadError;

      // 2. Create the document submission record
      const { data, error } = await supabase.from('document_submissions').insert({
        title: params.title,
        description: params.description || null,
        document_type: params.document_type,
        state: params.state || null,
        regulatory_authority: params.regulatory_authority || null,
        submission_period: params.submission_period || null,
        submission_year: params.submission_year || null,
        submission_quarter: params.submission_quarter || null,
        file_path: filePath,
        file_name: params.file.name,
        file_size: params.file.size,
        file_type: params.file.type,
        metadata: params.metadata || null,
        submitted_by: (await supabase.auth.getUser()).data.user?.id,
      }).select().single();

      if (error) throw error;
      
      return data;
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err.message || 'Failed to submit document',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDocumentStatus = async (id: string, status: string, reason?: string) => {
    try {
      setIsLoading(true);
      
      const updateData: any = { status };
      
      if (status === 'approved') {
        updateData.approved_by = (await supabase.auth.getUser()).data.user?.id;
        updateData.approved_at = new Date().toISOString();
      } else if (status === 'rejected' && reason) {
        updateData.rejection_reason = reason;
      }
      
      const { data, error } = await supabase
        .from('document_submissions')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return data;
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err.message || 'Failed to update document status',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getDocumentUrl = async (filePath: string) => {
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(filePath, 3600); // 1 hour expiry
    
    if (error) throw error;
    return data.signedUrl;
  };

  const documentMutation = useMutation({
    mutationFn: submitDocument,
    onSuccess: () => {
      toast({
        title: "Document Submitted",
        description: "Your document has been successfully submitted for review.",
      });
    }
  });

  const statusMutation = useMutation({
    mutationFn: (params: { id: string, status: string, reason?: string }) => 
      updateDocumentStatus(params.id, params.status, params.reason),
    onSuccess: (data) => {
      toast({
        title: `Document ${data.status.charAt(0).toUpperCase() + data.status.slice(1)}`,
        description: `The document has been ${data.status}.`,
      });
    }
  });

  return {
    submitDocument: documentMutation.mutate,
    updateDocumentStatus: statusMutation.mutate,
    getDocumentUrl,
    isSubmitting: documentMutation.isPending,
    isUpdating: statusMutation.isPending,
    isLoading,
    fetchDocuments
  };
}
